import { Col, Container, Row, Space, Typography, getTypographyDefaultColor } from '@ag1/ui';
import { toPlainText } from '@portabletext/react';
import { navigationClickEvent } from 'analytics/events/studio';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import ModalYoutubeVideos from 'shared/ui/ModalYoutubeVideos/ModalYoutubeVideos';
import { SanityKeyed, VideoReviews as VideoReviewsProps } from 'types/studio/studio';

import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { CommonSectionProps } from '../types';
import { VideoReviewsButton } from './VideoReviewsButton';
import { VideoReviewsRating } from './VideoReviewsRating';
import { VideoReviewsSlider } from './VideoReviewsSlider';

import styles from './VideoReviews.module.scss';

const VideoReviews = (props: SanityKeyed<VideoReviewsProps> & CommonSectionProps) => {
  const {
    ratingValue,
    ratingTitle,
    headline,
    showSliderButtons,
    unifiedButton,
    videoReviews,
    footnote,
    padding,
    visibleOnPageLoad,
    _key: key,
    _type: sectionType
  } = props;
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const handleItemClick = useCallback((index: number) => {
    setCurrentVideoIndex(index);
    setVideoModalOpen(true);

    const reviewCardTitle = videoReviews[index].title;

    navigationClickEvent({
      location: `${sectionType} video ${index + 1}`,
      navigationType: 'video',
      clickText: 'play',
      header: headline ? toPlainText(headline) : '',
      subheader: reviewCardTitle ? toPlainText(reviewCardTitle) : ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentVideoUrlPseudoArray = useMemo(
    () => [videoReviews[currentVideoIndex].video],
    [currentVideoIndex, videoReviews]
  );

  return (
    <Container narrow id={key}>
      <Space
        top={{ xxs: 64, s: 100, m: 120, l: 180 }}
        bottom={{ xxs: 40, s: 60, m: 80, l: 100 }}
        className={clsx(getSectionPaddingClasses(styles, padding))}>
        <Space bottom={16}>
          <Space top={{ xxs: 0, l: 16 }} bottom={{ xxs: 0, l: 16 }}>
            <VideoReviewsRating ratingValue={ratingValue} ratingTitle={ratingTitle} />
          </Space>
        </Space>
        <Row>
          <Col span={{ xxs: 12, l: 4, xl: 5 }}>
            <Typography
              color={getTypographyDefaultColor('new')}
              variant="headlineSans"
              size="2"
              noPadding>
              <CustomPortableText value={headline} tag="h2" />
            </Typography>
            <VideoReviewsButton
              unifiedButton={unifiedButton}
              sectionType={sectionType}
              className={styles.buttonUpLarge}
            />
          </Col>
          <Col span={{ xxs: 12, l: 8, xl: 7 }}>
            <Space top={{ xxs: 24, s: 40, m: 48, l: 0 }} bottom={{ xxs: 24, s: 40, m: 48, l: 0 }}>
              <VideoReviewsSlider
                videoReviews={videoReviews}
                showSliderButtons={showSliderButtons}
                onItemClick={handleItemClick}
                className={styles.slider}
                visibleOnPageLoad={visibleOnPageLoad}
              />
              {footnote && (
                <Typography variant="portalText" size="2" noPadding className={styles.footnote}>
                  <CustomPortableText value={footnote} />
                </Typography>
              )}
            </Space>
          </Col>
        </Row>
        <VideoReviewsButton
          unifiedButton={unifiedButton}
          sectionType={sectionType}
          className={styles.buttonDownLarge}
        />
      </Space>
      <ModalYoutubeVideos
        videoUrls={currentVideoUrlPseudoArray}
        isOpen={isVideoModalOpen}
        onClose={() => setVideoModalOpen(false)}
      />
    </Container>
  );
};

export default VideoReviews;
